import {hpcAttrs} from '@github-ui/hpc'
import {Banner, Link, Pagination, SplitPageLayout} from '@primer/react'
import {clsx} from 'clsx'
import Code from '../components/results/Code'
import Commits from '../components/results/Commits'
import Discussions from '../components/results/Discussions'
import LegacyCode from '../components/results/LegacyCode'
import Issues from '../components/results/Issues'
import Topics from '../components/results/Topics'
import MarketplaceListings from '../components/results/MarketplaceListings'
import Packages from '../components/results/Packages'
import PullRequests from '../components/results/PullRequests'
import Repositories from '../components/results/Repositories'
import Users from '../components/results/Users'
import Wikis from '../components/results/Wikis'

import {
  BookIcon,
  CodeIcon,
  CommentDiscussionIcon,
  GitCommitIcon,
  IssueOpenedIcon,
  MilestoneIcon,
  PeopleIcon,
  GitPullRequestIcon,
  LibraryIcon,
  PackageIcon,
  RepoIcon,
  TelescopeIcon,
} from '@primer/octicons-react'

import type React from 'react'
import {useCallback, useEffect, useMemo} from 'react'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import HoverCard from '../components/HoverCard'
import {useLocation, useNavigation} from '@github-ui/react-router'
import {getUrl, searchPath} from '@github-ui/paths'
import {FacetsPane} from '../components/FacetsPane/FacetsPane'
import NoResults from '../components/NoResults/NoResults'
import SearchError from '../components/SearchError'
import {SearchSubHeader} from '../components/SearchSubHeader'
import CodeItemsSkeleton from '../components/skeletons/CodeItemsSkeleton'
import PrimarySuggestions from '../components/suggestions/PrimarySuggestions'
import SecondarySuggestions from '../components/suggestions/SecondarySuggestions'
import {useSearchResultCounts, type SearchKind} from '../hooks/use-search-result-counts'
import {useSoftNavigate} from '../hooks/use-soft-navigate'
import type {SearchResults, HomePayload, SearchResultsType} from '../types/blackbird-types'
import {assertNever} from '../utilities/assert-never'

import Home from './Home'
import {
  chooseSearchType,
  mapSearchTypeToURLParam,
  parseString,
  SearchType,
  type SearchTypeURLParameter,
} from '@github-ui/blackbird-query-parsing/parsing'
import {extractRepoOrgScopes} from '@github-ui/blackbird-query-parsing/common'
import SearchTypeError, {getSearchTypeError, type SearchTypeErrorMessage} from '../components/SearchTypeError'
import {useSearchAnalytics} from '../hooks/use-search-analytics'
import LoggedInContext from '../contexts/LoggedInContext'
import {blackbirdSearchRoute} from '../routes/search-route'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {AIResourceSearch} from '../components/ai-resource-results/AIResourceSearch'

import styles from './Search.module.css'

export function SearchEntrypoint() {
  const {data: payload} = useRouteQuery(blackbirdSearchRoute, 'mainQuery')
  const location = useLocation()
  const navigation = useNavigation()
  const isNavigating = Boolean(
    navigation.location &&
    (navigation.location.pathname !== location.pathname || navigation.location.search !== location.search),
  )

  return (
    <Search
      payload={isNavigating ? null : payload}
      pendingSearch={isNavigating ? navigation.location?.search : undefined}
      loggedIn={!!payload?.logged_in}
    />
  )
}

function Search({
  payload,
  pendingSearch,
  loggedIn,
}: {
  payload: SearchResults | HomePayload | null
  pendingSearch?: string
  loggedIn: boolean
}) {
  const location = useLocation()
  const navigateToPage = useSoftNavigate(searchPath)

  const locationSearch = pendingSearch ?? location.search
  const search = useMemo(() => new URLSearchParams(locationSearch), [locationSearch])
  const searchType = search.get('type')
  const searchQuery = search.get('q')
  const scopes = search.get('saved_searches')
  const aiResourceSearchEnabled = isFeatureEnabled('ai_resource_search')
  const {sendSearchNextPageNavigation, sendSearchBackNavigation} = useSearchAnalytics()

  // Determine whether the user reached the page via back button
  useEffect(() => {
    const entries = window?.performance?.getEntries && window.performance.getEntries()
    if (entries && entries.length > 0 && (entries[0] as PerformanceNavigationTiming).type === 'back_forward') {
      if (payload?.type === 'code') {
        sendSearchBackNavigation(payload?.query_id)
      }
    }
  }, [sendSearchBackNavigation, payload])

  const isLoading = !payload
  const repoSearch = useMemo(() => {
    const astNode = parseString(searchQuery || '')
    if (payload?.type === 'home') {
      return false
    }

    // Only return true if we are scoped to exactly one repository
    const repoScopes = extractRepoOrgScopes(astNode)
    if (repoScopes.length === 1 && repoScopes[0]?.kind === 'repo') {
      return true
    }

    return false
  }, [searchQuery, payload?.type])

  const searchKinds = useMemo(
    () =>
      [
        {
          name: 'code',
          readableName: 'code',
          readableNamePlural: 'code',
          icon: CodeIcon,
        } as const,
        repoSearch
          ? null
          : ({
              name: 'repositories',
              readableName: 'repository',
              readableNamePlural: 'repositories',
              icon: RepoIcon,
            } as const),
        {
          name: 'issues',
          readableName: 'issue',
          readableNamePlural: 'issues',
          icon: IssueOpenedIcon,
        } as const,
        {
          name: 'pullrequests',
          readableName: 'pull request',
          readableNamePlural: 'pull requests',
          icon: GitPullRequestIcon,
        } as const,
        !repoSearch && aiResourceSearchEnabled
          ? ({
              name: 'ai_resources',
              readableName: 'AI resource',
              readableNamePlural: 'AI resources',
              icon: LibraryIcon,
            } as const)
          : null,
        {
          name: 'discussions',
          readableName: 'discussion',
          readableNamePlural: 'discussions',
          icon: CommentDiscussionIcon,
        } as const,
        repoSearch
          ? null
          : ({name: 'users', readableName: 'user', readableNamePlural: 'users', icon: PeopleIcon} as const),
        {name: 'commits', readableName: 'commit', readableNamePlural: 'commits', icon: GitCommitIcon} as const,
        {
          name: 'registrypackages',
          readableName: 'package',
          readableNamePlural: 'packages',
          icon: PackageIcon,
        } as const,
        {name: 'wikis', readableName: 'wiki', readableNamePlural: 'wikis', icon: BookIcon} as const,
        repoSearch
          ? null
          : ({name: 'topics', readableName: 'topic', readableNamePlural: 'topics', icon: MilestoneIcon} as const),
        repoSearch
          ? null
          : ({
              name: 'marketplace',
              readableName: 'marketplace',
              readableNamePlural: 'marketplace',
              icon: TelescopeIcon,
            } as const),
      ].filter((x: SearchKind | null): x is SearchKind => x !== null) as SearchKind[],
    [aiResourceSearchEnabled, repoSearch],
  )
  const resultKind = searchKinds.find(kind => kind.name === searchType) || searchKinds[0]

  const [isLoadingCounters, counts] = useSearchResultCounts({
    searchType: searchType as SearchResultsType,
    kinds: searchKinds,
    query: searchQuery,
    scopes,
    payload: payload as SearchResults,
  })

  const searchTypeError = useMemo(() => {
    if (!searchType && searchQuery && payload?.type !== 'home') {
      return null
    }
    return getSearchTypeError(searchType as SearchTypeURLParameter, searchQuery as string, !!payload?.logged_in)
  }, [searchType, searchQuery, payload])

  useEffect(() => {
    if (!searchType && searchQuery && payload?.type !== 'home') {
      // No search type is specified in the URL - that probably means somebody URL hacked
      // their way here. Choose a search type based on the query and redirect to there.
      let query = search.get('q')
      const ast = parseString(query || '')
      const preferredType = chooseSearchType(ast, !!payload?.logged_in)

      // Since they're URL hacking, they may have specified language using `&l=...`. In that case
      // rewrite it into the query itself. The `&l` parameter only has an effect in non-code search.
      if (preferredType === 'code') {
        let lang = search.get('l')
        if (lang) {
          if (lang.includes(' ')) lang = `"${lang}"`
          query = `${query} lang:${lang}`
        }
      }

      // We tried, and still don't know the preferred type. Default to code search and don't redirect or else
      // we will get caught in a loop.
      if (preferredType !== SearchType.Unknown) {
        navigateToPage(
          undefined,
          {q: query, type: mapSearchTypeToURLParam(preferredType), l: undefined, p: undefined},
          {replace: true},
        )
      }
    }
  }, [payload, search, searchType, searchQuery, navigateToPage])

  const onPageChange = useCallback(
    (e: React.MouseEvent, p: number) => {
      window.scrollTo(0, 0)
      navigateToPage(undefined, {p})
      if (payload?.type === 'code') {
        sendSearchNextPageNavigation(payload.query_id, p)
      }
      e.preventDefault()
    },
    [navigateToPage, sendSearchNextPageNavigation, payload],
  )

  if (payload?.type === 'home') {
    return <Home payload={payload} />
  }

  const isLoggedOutCodeResults = !payload?.logged_in && payload?.type === 'code'
  const type = payload?.type
  const selectedType = type || searchType?.toLowerCase()
  const isCode = type === 'code'
  const isAIResources = aiResourceSearchEnabled && selectedType === 'ai_resources'
  const showPagination =
    payload && payload.page_count > 1 && !!payload.page && payload.page_count > 1 && !isLoggedOutCodeResults
  const hasErrors = (payload && payload.errors?.length > 0) || searchTypeError !== null

  let title = ''
  if (payload) {
    title += `${payload.type} `
  }
  title += `Search Results`
  if (searchQuery) {
    title += ` · ${searchQuery}`
  }

  if (!resultKind) return null

  if (isAIResources) {
    return (
      <AIResourceSearch
        counts={counts}
        isLoadingCounters={isLoadingCounters}
        payload={payload}
        query={searchQuery ?? ''}
        resultKind={resultKind}
      />
    )
  }

  return (
    <LoggedInContext value={loggedIn}>
      <div
        className={clsx(
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          'search-results-page',
          styles.searchResultsPage,
          hasErrors && styles.searchResultsPageWithErrors,
        )}
      >
        <style>{searchMatchCss}</style>
        <h1 className="sr-only">{title}</h1>
        <SplitPageLayout className={styles.SplitPageLayout}>
          <FacetsPane
            isLoading={isLoading}
            isLoadingCounters={isLoadingCounters}
            searchKinds={counts}
            resultCount={payload && payload.result_count}
            selectedType={selectedType}
            facets={payload && payload.facets}
            searchType={payload && payload.type}
          />
          <SplitPageLayout.Content as="div" padding="none" width="full" className={styles.SplitPageLayout_Content}>
            <TwoColumnLayout
              fullWidth={isCode || !payload || isEmpty(payload)}
              fullHeight={isLoggedOutCodeResults}
              primarySuggestion={payload && <PrimarySuggestions payload={payload} />}
              secondarySuggestion={payload && <SecondarySuggestions payload={payload} />}
              header={
                payload &&
                !isAIResources && (
                  <SearchSubHeader
                    payload={payload}
                    isLoading={isLoading}
                    isLoadingCounters={isLoadingCounters}
                    searchKinds={counts}
                    selectedType={selectedType}
                  />
                )
              }
              notices={[
                payload && <Errors key="errors" errorType={searchTypeError} payload={payload} />,
                payload?.warn_limited_results && (
                  <Banner key="warn_limited_results" variant="warning" hideTitle title="Advanced search suggestion">
                    <Banner.Description>
                      Not finding the results your looking for? Try adding the <code>org:</code>, <code>repo:</code>, or{' '}
                      <code>user:</code> parameters for a more precise set of search results. To see all the parameters
                      check out our{' '}
                      <Link inline href="/search/advanced">
                        advanced search tool
                      </Link>
                    </Banner.Description>
                  </Banner>
                ),
              ]}
              pagination={
                showPagination && (
                  <div className={styles.paginationWrapper}>
                    <Pagination
                      pageCount={payload.page_count}
                      currentPage={payload.page}
                      onPageChange={onPageChange}
                      hrefBuilder={p => getUrl(searchPath, undefined, {p}).href}
                    />
                  </div>
                )
              }
              columnLeft={
                <>
                  {isAIResources ? null : !payload ? (
                    <CodeItemsSkeleton />
                  ) : isEmpty(payload) && !isLoggedOutCodeResults ? (
                    <NoResults resultKind={resultKind} searchKinds={counts} isLoadingCounters={isLoadingCounters} />
                  ) : (
                    <div
                      {...hpcAttrs()}
                      className={clsx(styles.dataResults, isLoggedOutCodeResults && styles.dataResultsFullHeight)}
                    >
                      <SearchResultsComponent payload={payload} isRepoSearch={repoSearch} />
                    </div>
                  )}
                </>
              }
            />
          </SplitPageLayout.Content>
        </SplitPageLayout>
        <HoverCard />
      </div>
    </LoggedInContext>
  )
}

function TwoColumnLayout({
  columnLeft,
  primarySuggestion,
  secondarySuggestion,
  header,
  fullWidth,
  fullHeight,
  notices,
  pagination,
}: {
  header: React.ReactNode
  columnLeft: React.ReactNode
  primarySuggestion?: React.ReactNode
  secondarySuggestion?: React.ReactNode
  notices: Array<React.ReactNode | null>
  fullWidth: boolean
  fullHeight?: boolean
  pagination?: React.ReactNode
}) {
  return (
    <div className={clsx(styles.twoColumnOuter, fullHeight && styles.twoColumnOuterFullHeight)}>
      <div className={clsx(styles.mainColumn, fullHeight && styles.mainColumnFullHeight)}>
        {header}
        <div className={styles.noticesContainer}>{notices}</div>
        <div className={styles.primarySuggestionMobile}>{primarySuggestion}</div>
        <div className={clsx(styles.columnLeftWrapper, fullHeight && styles.columnLeftWrapperFullHeight)}>
          {columnLeft}
        </div>
        <div className={styles.paginationSection}>{pagination}</div>
        {!fullWidth && <div className={styles.secondarySuggestionMobile}>{secondarySuggestion}</div>}
      </div>
      {!fullWidth && (
        <div className={styles.rightSidebar}>
          {primarySuggestion}
          {secondarySuggestion}
        </div>
      )}
    </div>
  )
}

function Errors({errorType, payload}: {errorType: SearchTypeErrorMessage | null; payload: SearchResults}) {
  if (errorType) {
    return <SearchTypeError {...errorType} />
  }

  if (payload?.errors && payload.errors.length > 0) {
    // Show at most two errors. More than that is overwhelming
    return (
      <>
        {payload.errors.slice(0, 2).map((error, idx) => {
          // eslint-disable-next-line @eslint-react/no-array-index-key
          return <SearchError error={error} key={idx} index={idx} />
        })}
      </>
    )
  }
  return null
}

function SearchResultsComponent({payload, isRepoSearch}: {payload: SearchResults; isRepoSearch: boolean}) {
  switch (payload.type) {
    case 'code':
      return <Code isRepoSearch={isRepoSearch} results={payload} />
    case 'codelegacy':
      return <LegacyCode results={payload} />
    case 'commits':
      return <Commits results={payload} />
    case 'discussions':
      return <Discussions results={payload} />
    case 'topics':
      return <Topics results={payload} />
    case 'registrypackages':
      return <Packages results={payload} />
    case 'repositories':
      return <Repositories results={payload} />
    case 'users':
      return <Users results={payload} />
    case 'wikis':
      return <Wikis results={payload} />
    case 'issues':
      return <Issues results={payload} />
    case 'pullrequests':
      return <PullRequests results={payload} />
    case 'marketplace':
      return <MarketplaceListings results={payload} />
    case 'ai_resources':
      return null
    default:
      assertNever(payload)
  }
}

const searchMatchCss = `
  .search-results-page .search-match em {
    font-style: normal;
    font-weight: 600;
  }

  .search-results-page .search-title .search-match em {
    font-style: normal;
    font-weight: 700;
  }
`

function isEmpty(payload: SearchResults) {
  return payload.results?.length === 0
}
